import { updateAnalyticsUserCompany } from '@/lib/analytics'
import CompanyService from '@/services/CompanyService'
import { get, cloneDeep } from 'lodash-es'
import Config from '@/config'

export const setUserActiveCompanyId = (userId, companyId) =>
  window.localStorage.setItem(userId, companyId)

export const getUserActiveCompanyId = (userId) => window.localStorage.getItem(userId)

export const removeUserActiveCompanyId = (userId) => window.localStorage.removeItem(userId)

// The location store's active-company cache is only cleared here. Skipped on the initial set (every
// page fetches on mount), on the same-id re-dispatch from refreshCompanyList, and when the company
// lookup failed (nothing to fetch for, so the old cache is left rather than emptied). Awaited so a
// caller that awaits the switch and then reads the cache sees the new company's locations, not [].
const syncLocationsForCompanyChange = async (dispatch, previousCompanyId, activeCompanyId) => {
  if (!previousCompanyId || !activeCompanyId || previousCompanyId === activeCompanyId) return

  dispatch('location/resetUserActiveCompanyLocations', null, { root: true })
  try {
    await dispatch('location/getUserLocationsByActiveCompany', null, { root: true })
  } catch (error) {
    console.error('Failed to refetch locations after company switch', error)
  }
}

export default {
  namespaced: true,
  state: {
    activeCompany: undefined,
    activeSettingsTab: undefined,
    companies: [],
    textUsage: {
      // deprecated: use campaignSetups now
      textCount: 0,
      totalCount: 1000
    },
    employees: [],
    companyLegalModalShown: false
  },
  getters: {
    companies: (state) => state.companies,
    loyaltyEnabled(s) {
      const { loyalty = {} } = s.activeCompany || {}
      return loyalty ? !!loyalty.enabled : false
    },
    activePartnerId(s) {
      return (s.activeCompany || {}).partner || null
    },
    activeCompany(state) {
      return state.activeCompany
    },
    selectEmployees(state) {
      return state.employees
    },
    selectEmployee: (state) => (id) => {
      return state.employees.find((e) => e._id === id)
    },
    selectCompanies(state) {
      return state.companies
    },
    selectOldestCompanyStartDate: (state) => (ids) => {
      let selectedCompanies = state.companies.filter((c) => ids.includes(c._id))
      if (!selectedCompanies.length) {
        selectedCompanies = state.companies
      }
      return selectedCompanies.reduce((previous, current) =>
        current.created < previous.created ? current : previous
      ).created
    },
    selectCompany: (state) => (id) => {
      return state.companies.find((c) => c._id === id)
    },
    selectCompanyLegalModalShown: (state) => {
      return state.companyLegalModalShown
    },
    selectShowNotesSettings: (state) => {
      return !!state.companies.filter((c) => c.settings.internalNotesEnabled).length
    },
    selectGoalsEnabled: (state) => {
      return !!state.companies.filter((c) => c.settings?.goals?.enabled).length
    },
    selectGoalsSettings: (state) => {
      const company = state.companies.find((c) => c.settings?.goals?.enabled)
      return company?.settings?.goals || {}
    },
    selectIsPermitted: (state, getters, rootState, rootGetters) => (element) => {
      const role = rootGetters['user/onlineUser']?.role
      const permissions = state.activeCompany?.permissions
      if (!permissions || !permissions[element]) return true
      if (!role) return false
      if (role === 'admin') return true
      return permissions[element].includes(role)
    },
    // The flag is the engineering rollout and kill switch; the company setting is the customer
    // entitlement. Both must hold, and a missing setting reads as off.
    selectTicketingEnabled: (state, getters, rootState, rootGetters) =>
      rootGetters['featureFlag/isEnabledV2']('TICKETING') &&
      state.activeCompany?.settings?.ticketingEnabled === true,
    selectInsightsCompanies: (state) => state.companies.filter((c) => c.insightsEnabled),
    selectActiveCompany: (state) => state.activeCompany,
    selectTextUsage: (state) => state.textUsage,
    selectRelevantCompanies: (state, getters, rootState, rootGetters) => {
      // returns for organizations if all_companies, else falls back to standard companies logic
      let companies = []
      const organizationCompanies = rootGetters['organization/selectOrganizationCompanies']
      if (state.activeCompany?._id === 'ALL_COMPANIES') {
        companies = organizationCompanies.length ? organizationCompanies : state.companies
      } else if (state.activeCompany?._id) {
        companies = [state.activeCompany] || []
      }
      return companies
    },
    selectSurveyFraudPreventionEnabled: (state, getters) => {
      return !!getters.selectRelevantCompanies.filter(
        (c) => c.settings?.surveyFraudPrevention?.enabled
      ).length
    },
    selectSuspiciousUiVisible: (state, getters) => {
      return !!getters.selectRelevantCompanies.filter(
        (c) =>
          c.settings?.surveyFraudPrevention?.enabled &&
          (c.settings?.surveyFraudPrevention?.mode ?? 'flag') !== 'block'
      ).length
    }
  },
  mutations: {
    setCompanies(state, companies) {
      state.companies = [...companies]
    },
    setActiveCompany(state, company) {
      state.activeCompany = company
    },
    setActiveSettingsTab(state, tab) {
      state.activeSettingsTab = tab
    },
    setTextUsage(state, usage) {
      state.textUsage = usage
    },
    setEmployees(state, employees) {
      state.employees = employees
    },
    SET_COMPANY_LEGAL_MODAL_SHOWN(state, companyLegalModalShown) {
      state.companyLegalModalShown = companyLegalModalShown
    },
    SET_ALL_COMPANIES(state) {
      state.activeCompany = { name: 'All Companies', _id: 'ALL_COMPANIES' }
    }
  },
  actions: {
    async setAllCompanies({ state, commit, dispatch }) {
      const previousCompanyId = state.activeCompany?._id
      commit('SET_ALL_COMPANIES')
      await syncLocationsForCompanyChange(dispatch, previousCompanyId, 'ALL_COMPANIES')
    },

    async refreshCompanyList({ state, rootState, commit, dispatch }) {
      const { onlineUser } = rootState.user

      if (!onlineUser) return

      const response = await CompanyService.list(rootState.user.onlineUser._id)
      const { companies } = response.body

      commit('setCompanies', companies)

      if (!state.activeCompany) {
        const activeCompanyId = getUserActiveCompanyId(onlineUser._id)

        if (activeCompanyId == 'ALL_COMPANIES') {
          dispatch('setActiveCompany', { name: 'All Companies', _id: 'ALL_COMPANIES' })
        } else {
          const companyMatch = companies.find((c) => c._id === activeCompanyId)
          const activeCompany = companyMatch ? companyMatch : companies[0]
          dispatch('setActiveCompany', activeCompany)
        }
      } else {
        if (state.activeCompany._id != 'ALL_COMPANIES') {
          const updatedActiveCompany = companies.find(
            (company) => company._id === state.activeCompany._id
          )
          dispatch('setActiveCompany', updatedActiveCompany)
        }
      }
    },
    async setActiveCompany({ state, rootState, commit, dispatch }, company) {
      const { onlineUser } = rootState.user
      const previousCompanyId = state.activeCompany?._id

      const activeCompany = cloneDeep(company)
      commit('setActiveCompany', activeCompany)
      const locationsSynced = syncLocationsForCompanyChange(
        dispatch,
        previousCompanyId,
        activeCompany?._id
      )

      if (activeCompany) {
        if (onlineUser) {
          setUserActiveCompanyId(onlineUser._id, activeCompany._id)
        }

        updateAnalyticsUserCompany(this._vm.$intercom, activeCompany)
      }

      dispatch('analytics/refreshMetrics', {}, { root: true })
      dispatch('review/fetchOldestTimestamp', {}, { root: true })
      await locationsSynced
    },
    async queryTextUsage({ state, commit }) {
      const response = await CompanyService.textUsage(state.activeCompany._id)
      const usage = { ...response.data }
      const creditBalance = state.activeCompany.textCreditBalance || 0
      usage.totalCount += creditBalance
      commit('setTextUsage', usage)
    },
    async fetchEmployees({ commit, rootState }) {
      const { _id: userId } = rootState.user.onlineUser
      const { _id: companyId } = rootState.company.activeCompany

      const response = await CompanyService.getTeam({ userId, companyId })

      if (response && response.body) {
        const { employees } = response.body
        commit('setEmployees', employees)
      }
    },
    async createCompany({ commit }, payload) {
      const response = await CompanyService.create(payload)
      return response
    },
    async createOauthAccount({ commit }, payload) {
      const response = await CompanyService.addOauth(payload)
      return response
    },
    async updateCompanyLegalData({ dispatch }, payload) {
      const loadingUuid = await dispatch(
        'addToLoader',
        { name: 'UPDATE_COMPANY_LEGAL_DATA' },
        { root: true }
      )
      let result = { success: true }
      try {
        const response = await CompanyService.updateCompanyLegal(payload)
        if (!response.body.success) {
          result.success = false
        }
      } catch (e) {
        if (e.body.message && e.body.message.startsWith('instance.body.zip')) {
          result.message = 'Sorry, Invalid Zip/Postal Code.'
        }

        if (e.body.message && e.body.message.startsWith('instance.body.corporateWebsite')) {
          result.message = 'Sorry, Invalid Corporate Website.'
        }
        result.success = false
      }
      await dispatch('removeFromLoader', loadingUuid, { root: true })
      return result
    },
    setCompanyLegalModalShown({ commit }, value) {
      commit('SET_COMPANY_LEGAL_MODAL_SHOWN', true)
    }
  }
}
